// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../api';
import { identify, resetIdentity } from '../utils/dataLayer';
import { reconcileShortlist } from '../services/shortlistReconcile';
import { applyPendingReferral } from '../services/referralService';
import { syncSessionHint } from '../utils/sessionHint';
import { clearUrlStash } from '../utils/urlStash';
import {
  isMedicalStudentProvider,
  providerTrainingStage,
  stageFirstProviderLabel,
} from '../utils/providerRegistrationRole';

let __bootstrapFiredForToken = null;
let __bootstrapSeeder = null; // lazy-loaded to avoid a static cycle with the service

/**
 * Fire `/provider/bootstrap` for a freshly-authenticated provider so the
 * axios cache is primed before any page mounts. Deduplicated per-token so
 * rapid setUser → refreshUser sequences don't double-fetch.
 */
function kickoffProviderBootstrap(user) {
  try {
    const role = user?.role;
    if (role !== 'CRNA' && role !== 'ADMIN') return;
    if (isMedicalStudentProvider(user)) return;
    const token = typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('authToken')
      : null;
    if (!token) return;
    if (__bootstrapFiredForToken === token) return;
    __bootstrapFiredForToken = token;
    const loadSeeder = __bootstrapSeeder
      ? Promise.resolve(__bootstrapSeeder)
      : import('../services/unifiedDashboardService').then((m) => {
        __bootstrapSeeder = m.fetchUnifiedDashboardData;
        return m.fetchUnifiedDashboardData;
      });
    loadSeeder
      .then((fetcher) => fetcher(token))
      .catch(() => { /* bootstrap is an optimization, never surface errors */ });
  } catch { /* ignore */ }
}

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

/**
 * Is this app role a provider? Provider types (CRNA, CAA, SRNA, anesthesiologist)
 * all sign in as role CRNA; the type lives in providerType. The backend has no
 * other provider role. One definition so a surface never re-derives it.
 * Recruiters (GROUP), ADMIN and MARKETING are never providers.
 */
export const isProviderRole = (role) => role === 'CRNA';

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null); // full user data from /auth/me
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Admin act-as-company: { groupId, name } while impersonating, else null.
  const [impersonation, setImpersonation] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('impersonation') || 'null');
    } catch {
      return null;
    }
  });

  // Ends the session. The app calls this itself when a stored session turns out
  // to be expired or rejected (see loadAuthState and fetchUserProfile), which
  // can happen on the very load that brings a magic link or an invite: the page
  // that reads the stashed token or code may not have mounted yet (it is a lazy
  // chunk), so this must leave the URL stash alone. `logout` below is the one a
  // user's own sign-out uses.
  const endSession = useCallback(() => {
    syncSessionHint(null, null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('groupApprovalCache');
    localStorage.removeItem('impersonation');
    localStorage.removeItem('adminTokenBackup');
    // The route guard's per-user "profile set up" cache must not outlive the
    // session, or the next account in this tab could skip the profile walk.
    try {
      Object.keys(sessionStorage)
        .filter((k) => k === 'pv_profile_setup' || k.startsWith('pv_profile_setup:'))
        .forEach((k) => sessionStorage.removeItem(k));
    } catch { /* storage blocked */ }
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setImpersonation(null);
    resetIdentity();
  }, []);

  // A user's own sign-out. Whatever the GTM bootstrap stashed out of a URL for
  // this session (an invite code, an OAuth email) must not be readable by the
  // next account in this tab.
  const logout = useCallback(() => {
    clearUrlStash();
    endSession();
  }, [endSession]);

  // Enter a managed account: stash the admin token, swap in the scoped token,
  // and hard-reload into the company app so every context re-inits as the GROUP.
  const enterImpersonation = useCallback((impToken, group) => {
    const current = localStorage.getItem('authToken');
    if (current) localStorage.setItem('adminTokenBackup', current);
    localStorage.setItem(
      'impersonation',
      JSON.stringify({ groupId: group.id, name: group.name }),
    );
    localStorage.setItem('authToken', impToken);
    window.location.assign('/company/dashboard');
  }, []);

  // Exit: restore the admin token and return to an admin-owned console. The
  // optional destination is intentionally restricted to /admin so a component
  // cannot turn account switching into an open redirect. React passes a click
  // event when this function is used directly as a handler; non-string values
  // keep the ordinary Managed Accounts destination.
  const exitImpersonation = useCallback((destination) => {
    const admin = localStorage.getItem('adminTokenBackup');
    if (admin) localStorage.setItem('authToken', admin);
    else localStorage.removeItem('authToken');
    localStorage.removeItem('adminTokenBackup');
    localStorage.removeItem('impersonation');
    const target =
      typeof destination === 'string' && /^\/admin(?:[/?#]|$)/.test(destination)
        ? destination
        : '/admin?tab=managed-accounts';
    window.location.assign(target);
  }, []);

  const fetchUserProfile = useCallback(async (authToken) => {
    try {
      // Route through shared axios instance so this call seeds the dedupe/cache
      // layer (api.js) — sibling components hitting /auth/me reuse this response.
      const response = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${authToken}` },
      }).then(
        (r) => ({ ok: true, status: r.status, json: async () => r.data }),
        (err) => {
          const status = err?.response?.status || 0;
          return { ok: false, status, json: async () => err?.response?.data };
        },
      );

      if (response.ok) {
        const userData = await response.json();
        console.log('Fetched full user profile:', userData);

        // Detect non-approved → approved transition so the UI can reset cleanly.
        // All of our gates (GroupApprovalGate, GroupCoreContext's
        // hasLoadedInitialGroup, CompanyContext, axios interceptor cache,
        // sessionStorage dismissal keys) are seeded once and not all reactive,
        // so a hard reload is the simplest way to flush stale state.
        let approvalFlipped = false;
        try {
          if (userData?.role === 'GROUP') {
            const prev = JSON.parse(localStorage.getItem('groupApprovalCache') || 'null');
            const newStatus = userData.group?.reviewStatus;
            if (prev && prev.reviewStatus !== 'approved' && newStatus === 'approved') {
              approvalFlipped = true;
            }
          }
        } catch (e) { /* ignore */ }

        setUser(userData);
        const trainingStage = providerTrainingStage(userData);
        const canonicalProviderType =
          userData.providerProfile?.providerType || userData.providerType || null;
        // Push identity to dataLayer for GA4 cross-device stitching
        identify({
          id: userData._id || userData.id || userData.sub,
          type: userData.role === 'GROUP' ? 'group' :
            isProviderRole(userData.role) ? 'provider' :
            userData.role === 'ADMIN' ? 'admin' : 'unknown',
          providerType: stageFirstProviderLabel(
            canonicalProviderType,
            trainingStage,
          ),
          trainingStage,
          groupType: userData.group?.organizationType || userData.organizationType || null,
          subscriptionTier: userData.group?.subscriptionTier || userData.subscriptionTier || 'free',
        });
        // Cache approval state synchronously so axios interceptor can read it
        // before dispatching a request. Keeps the blocklist out of React land.
        try {
          if (userData?.role === 'GROUP') {
            const g = userData.group || {};
            localStorage.setItem(
              'groupApprovalCache',
              JSON.stringify({
                role: userData.role,
                reviewStatus: g.reviewStatus || 'pending',
                reviewReason: g.reviewReason || '',
                reviewNotes: g.reviewNotes || '',
                needsInfoFields: g.needsInfoFields || [],
              }),
            );
          } else {
            localStorage.removeItem('groupApprovalCache');
          }
        } catch (e) { /* ignore */ }

        if (approvalFlipped) {
          // Clear per-session modal dismissals (keys are scoped to previous status).
          try {
            Object.keys(sessionStorage).forEach((k) => {
              if (k.startsWith('underReviewDismissed:')) sessionStorage.removeItem(k);
            });
          } catch (e) { /* ignore */ }
          // Hard reload so every cache/context re-initializes with approved state.
          // Defer one tick so React commits the setUser above before we navigate.
          setTimeout(() => {
            try { window.location.reload(); } catch (e) { /* ignore */ }
          }, 0);
        }

        // Prefetch the provider bootstrap so every provider-area page — not
        // just the dashboard — renders from cache on mount. Fire-and-forget;
        // bootstrap seeds the axios cache via unifiedDashboardService.
        kickoffProviderBootstrap(userData);

        return userData;
      } else if (response.status === 401 || response.status === 403) {
        // Auth failure — token invalid or expired
        console.warn('Auth failure on profile fetch:', response.status);
        endSession();
        return null;
      } else {
        // Transient server error (500, etc.) — keep the session alive with basic token data
        console.warn('Transient error fetching user profile:', response.status, '— keeping session');
        return null;
      }
    } catch (err) {
      // Network error — don't log out, just warn
      // Network error — don't log out, just continue silently
      return null;
    }
  }, [endSession]);

  const login = useCallback(async (newToken) => {
    localStorage.setItem('authToken', newToken);
    try {
      const decoded = jwtDecode(newToken);
      console.log('Decoded token:', decoded);

      setToken(newToken);
      setIsAuthenticated(true);

      // Set basic user info from token immediately to prevent timing issues
      const basicUser = {
        id: decoded.sub,
        sub: decoded.sub,
        phone: decoded.phone,
        role: decoded.role,
        category: decoded.category,
        providerProfileId: decoded.providerProfileId,
        providerType: decoded.providerType,
        trainingStage: decoded.trainingStage,
        expectedMedicalSchoolGraduationYear:
          decoded.expectedMedicalSchoolGraduationYear,
      };
      setUser(basicUser);

      // Fetch full user profile including providerProfile
      const fullUser = await fetchUserProfile(newToken);

      // Claim any anonymous /providers-map saves into the account (idempotent,
      // no-op when the shortlist is empty). Best-effort: never blocks login.
      reconcileShortlist(newToken);

      console.log('Login complete');
      return fullUser || basicUser;
    } catch (err) {
      console.error("Invalid token", err);
      endSession();
      return null;
    }
  }, [fetchUserProfile, endSession]);

  // Check if user has the required role
  const hasRole = (requiredRoles) => {
    if (!user || !user.role) {
      console.log('No user or role found:', user);
      return false;
    }

    // Admin role has access to everything
    if (user.role === 'ADMIN') {
      console.log('User has ADMIN role, granting access');
      return true;
    }

    // If no specific roles are required, the user is authorized
    if (!requiredRoles || requiredRoles.length === 0) return true;

    // Check if user role is in the required roles
    const hasRequiredRole = requiredRoles.includes(user.role);
    console.log(`Checking if ${user.role} is in [${requiredRoles.join(', ')}]: ${hasRequiredRole}`);
    return hasRequiredRole;
  };

  useEffect(() => {
    const loadAuthState = async () => {
      setIsLoading(true);
      const storedToken = localStorage.getItem('authToken');

      if (storedToken) {
        try {
          const decoded = jwtDecode(storedToken);
          console.log('Loading stored token, decoded:', decoded);

          // Check if token is expired
          const currentTime = Date.now() / 1000;
          if (decoded.exp && decoded.exp < currentTime) {
            console.log('Token expired, logging out');
            endSession();
          } else {
            setToken(storedToken);
            setIsAuthenticated(true);

            // Set basic user info from token immediately (mirrors login() flow)
            // This ensures auth.user always has {role, sub} even if fetchUserProfile fails
            const basicUser = {
              id: decoded.sub,
              sub: decoded.sub,
              phone: decoded.phone,
              role: decoded.role,
              category: decoded.category,
              providerProfileId: decoded.providerProfileId,
              providerType: decoded.providerType,
              trainingStage: decoded.trainingStage,
              expectedMedicalSchoolGraduationYear:
                decoded.expectedMedicalSchoolGraduationYear,
            };
            setUser(basicUser);

            // Fetch full user profile including providerProfile
            await fetchUserProfile(storedToken);

            console.log('Auth state loaded');
          }
        } catch (err) {
          console.error("Invalid token", err);
          endSession();
        }
      } else {
        console.log('No auth token found in localStorage');
      }

      setIsLoading(false);
    };

    loadAuthState();
  }, [fetchUserProfile, endSession]);

  useEffect(() => {
    if (!isLoading) syncSessionHint(isAuthenticated ? token : null, user?.role);
  }, [isLoading, isAuthenticated, token, user?.role]);

  // Apply a pending /r/:code invite whenever a session starts: a login or an
  // app load with a stored token. For an existing account the backend refuses
  // (older than a day) and the stored code is dropped, so a code left in a
  // shared browser cannot credit the next person to register there. For a new
  // account whose first apply failed, this is the retry. Sends nothing when no
  // code is stored, never throws, and shares one request with signup's own call.
  useEffect(() => {
    if (token) applyPendingReferral();
  }, [token]);

  const refreshUser = async () => {
    if (token) {
      await fetchUserProfile(token);
    }
  };

  // Poll /auth/me while the GROUP user is non-approved so admin approvals show
  // up without a logout/login dance. Stops once approved. Also refreshes on tab
  // focus — cheap way to pick up changes the moment the user comes back.
  // Approved users don't poll: status only changes in one direction from their
  // perspective, and we'd just be spamming the API.
  useEffect(() => {
    if (!token || !user) return;
    if (user.role !== 'GROUP') return;
    const status = user.group?.reviewStatus;
    if (!status || status === 'approved') return;

    const POLL_MS = 30000;
    let intervalId = setInterval(() => {
      fetchUserProfile(token);
    }, POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchUserProfile(token);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token, user?.role, user?.group?.reviewStatus, fetchUserProfile]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        login,
        logout,
        hasRole,
        refreshUser,
        impersonation,
        enterImpersonation,
        exitImpersonation,
        auth: { token, user },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
